// AapkaNoteTab.tsx
import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { NotebookPen } from "lucide-react";
import AstriaIndiaV2Shell from "../AstriaIndiaV2Shell";
import {
  AstriaIndiaV2ResultCard,
  AstriaIndiaV2FieldBlock,
  AstriaIndiaV2HighlightBadge,
  AstriaIndiaV2SignatureLine,
  IndiaV2LoadingState,
  IndiaV2ErrorState,
  IndiaV2ChatCta,
} from "../AstriaIndiaV2Card";
import {
  WizardProgress,
  WizardStep,
  WizardNav,
  WizardEmojiWheel,
  WizardTextInput,
} from "../AstriaIndiaV2Wizard";
import { useAstriaIndiaV2Tab } from "../useAstriaIndiaV2Tab";
import {
  useChat,
  AstriaIndiaV2AapkaNoteData,
} from "../../../../contexts/ChatContext";
import { useIndiaV2Language } from "../../../../utils/India/astriaIndiaV2Language";
import { ASTRIA_INDIA_V2_COPY } from "../../../../utils/India/astriaIndiaV2Copy";

const SUBCATEGORY_NAME = "Aapka Note";
const TOTAL_STEPS = 2;

// Helper function to parse the response data. Matches both the V2 marker
// (<<<INDIA_V2_AAPKA_NOTE_DATA>>>) and V3's (<<<INDIA_V3_AAPKA_NOTE_DATA>>>)
// — V3's payload additionally carries highlight/micro_imagery/clarity_point,
// which JSON.parse picks up automatically since they're just extra keys.
const parseAapkaNoteResponse = (
  content: string,
): AstriaIndiaV2AapkaNoteData | null => {
  try {
    // Try to find the JSON data between the markers
    const match = content.match(
      /<<<INDIA_V[23]_AAPKA_NOTE_DATA>>>\s*({[\s\S]*?})\s*<<<END_INDIA_V[23]_AAPKA_NOTE_DATA>>>/,
    );
    if (match && match[1]) {
      return JSON.parse(match[1]);
    }

    // Alternative: Try to parse the entire response if it's already JSON
    const parsed = JSON.parse(content);
    if (parsed.reflection && parsed.gentle_note) {
      return parsed;
    }
    return null;
  } catch (error) {
    console.error("Error parsing Aapka Note response:", error);
    return null;
  }
};

const AapkaNoteTab: React.FC = () => {
  // Which engine to call is decided ONLY by which sidebar entry the user
  // clicked ("Astria India V2" vs "Astria India V3" — see
  // Sidebar.tsx/TesterHome.tsx's "?v=3" query flag) and never changes for
  // the lifetime of this page — no in-page switch.
  const [searchParams] = useSearchParams();
  const version: "v2" | "v3" = searchParams.get("v") === "3" ? "v3" : "v2";
  const categoryName = version === "v3" ? "Astria India V3" : "Astria India V2";

  const [lang, setLang] = useIndiaV2Language();

  const copy = ASTRIA_INDIA_V2_COPY[lang];
  const t = copy.aapkaNote;
  const shared = copy.shared;

  const { chats } = useChat();
  const { loading, error, message, send, reset, goToChat } =
    useAstriaIndiaV2Tab({
      categoryName,
      subCategoryName: SUBCATEGORY_NAME,
    });

  const latestUserMessage = useMemo(() => {
    let latest: { content: string; timestamp: Date } | null = null;
    for (const chat of chats) {
      for (const msg of chat.messages || []) {
        if (msg.sender !== "user") continue;
        const ts = new Date(msg.timestamp);
        if (!latest || ts > latest.timestamp) {
          latest = { content: msg.content, timestamp: ts };
        }
      }
    }
    return latest;
  }, [chats]);

  const [step, setStep] = useState(1);
  const [note, setNote] = useState(latestUserMessage?.content || "");
  const [feeling, setFeeling] = useState<string | null>(null);
  const [parsedData, setParsedData] =
    useState<AstriaIndiaV2AapkaNoteData | null>(null);

  const started = !!message || loading || !!error;

  // Parse the message content when it arrives. Falls back to the
  // structured astriaIndiaV2Data field (populated by the backend for both
  // V2 and V3) if the marker regex above didn't match for any reason.
  useEffect(() => {
    if (message?.content) {
      const parsed =
        parseAapkaNoteResponse(message.content) ||
        (message.astriaIndiaV2Data as AstriaIndiaV2AapkaNoteData | undefined) ||
        null;
      setParsedData(parsed);
    }
  }, [message]);

  const buildMessage = () => {
    const feelingLabel = feeling
      ? shared.emojiLabels[feeling as keyof typeof shared.emojiLabels] ||
        feeling
      : "unsettled";
    return `Here is something on my mind: "${note}". I feel ${feelingLabel} about it. Please give me a gentle Aapka Note reflection on this.`;
  };

  const handleSubmit = () => {
    setParsedData(null); // Clear previous data
    send(buildMessage());
  };

  const handleReset = () => {
    reset();
    setStep(1);
    setParsedData(null);
  };

  if (started) {
    return (
      <AstriaIndiaV2Shell
        active="aapka-note"
        lang={lang}
        onLangChange={setLang}
        version={version}
      >
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: "#2E1F45",
              marginBottom: 4,
            }}
          >
            {t.tabLabel}
          </div>
          <p style={{ fontSize: 13.5, color: "#8C7DA0" }}>
            {t.tabSublabel} — {t.resultTitle}
          </p>
        </div>

        {loading && <IndiaV2LoadingState label={t.loading} />}

        {!loading && error && (
          <IndiaV2ErrorState
            message={error}
            onRetry={handleSubmit}
            retryLabel={shared.tryAgain}
          />
        )}

        {!loading && !error && parsedData && (
          <>
            <AstriaIndiaV2ResultCard
              icon={<NotebookPen className="w-5 h-5" />}
              title={t.resultTitle}
              subtitle={t.tabLabel}
              accent={["#C9A9E8", "#6B3FA0"]}
              shareText={`${t.tabLabel}\n\n${parsedData.reflection}\n\n${parsedData.gentle_note}`}
              shareLabels={shared}
            >
              <AstriaIndiaV2HighlightBadge text={parsedData.highlight} />
              <AstriaIndiaV2FieldBlock
                label={t.yourNoteLabel}
                value={note || t.yourNoteFallback}
              />

              {/* Reflection Card */}
              <div
                style={{
                  marginTop: 16,
                  marginBottom: 16,
                  padding: "16px",
                  background:
                    "linear-gradient(135deg, #F8F4FF 0%, #F0E8FF 100%)",
                  borderRadius: "12px",
                  border: "1px solid #E8DDF5",
                }}
              >
                <h4
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#6B3FA0",
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span>💭</span> {t.reflectionLabel}
                </h4>
                <p
                  style={{
                    fontSize: 15,
                    lineHeight: 1.8,
                    color: "#4A3B5C",
                    margin: 0,
                  }}
                >
                  {parsedData.reflection}
                </p>
              </div>

              <AstriaIndiaV2SignatureLine value={parsedData.micro_imagery} />

              {/* Emotional Thread Card */}
              <div
                style={{
                  marginTop: 12,
                  padding: "16px",
                  background:
                    "linear-gradient(135deg, #E8F4FD 0%, #D4EAF7 100%)",
                  borderRadius: "12px",
                  border: "1px solid #C5DDEB",
                }}
              >
                <h4
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#2C6B8C",
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span>🌊</span> {t.emotionalThreadLabel}
                </h4>
                <p
                  style={{
                    fontSize: 15,
                    lineHeight: 1.8,
                    color: "#2C4A5C",
                    margin: 0,
                    fontStyle: "italic",
                  }}
                >
                  {parsedData.emotional_thread}
                </p>
              </div>

              <AstriaIndiaV2SignatureLine value={parsedData.clarity_point} emphasis />

              {/* Gentle Note Card */}
              <div
                style={{
                  marginTop: 12,
                  padding: "16px",
                  background:
                    "linear-gradient(135deg, #FFF8F0 0%, #FFF0E6 100%)",
                  borderRadius: "12px",
                  border: "1px solid #F5E6D5",
                }}
              >
                <h4
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#8B5A2B",
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span>🌙</span> {t.gentleNoteLabel}
                </h4>
                <p
                  style={{
                    fontSize: 15,
                    lineHeight: 1.8,
                    color: "#5C4033",
                    margin: 0,
                    fontStyle: "italic",
                  }}
                >
                  {parsedData.gentle_note}
                </p>
              </div>
            </AstriaIndiaV2ResultCard>

            <button
              onClick={handleReset}
              className="text-xs font-semibold transition-colors"
              style={{
                marginTop: 14,
                color: "#8C7DA0",
                background: "none",
                border: "none",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {t.reflectOnSomethingElse}
            </button>
            <IndiaV2ChatCta onClick={goToChat} label={shared.continueInChat} />
          </>
        )}

        {!loading && !error && !parsedData && message && (
          <AstriaIndiaV2ResultCard
            icon={<NotebookPen className="w-5 h-5" />}
            title={t.resultTitle}
            subtitle={t.tabLabel}
            accent={["#C9A9E8", "#6B3FA0"]}
          >
            <div
              style={{
                padding: "16px",
                background: "#F8F4FF",
                borderRadius: "12px",
                marginBottom: 12,
              }}
            >
              <p
                style={{
                  fontSize: 15,
                  lineHeight: 1.75,
                  color: "#4A3B5C",
                  whiteSpace: "pre-wrap",
                  margin: 0,
                }}
              >
                {message.content}
              </p>
            </div>

            <button
              onClick={handleReset}
              className="text-xs font-semibold transition-colors"
              style={{
                marginTop: 14,
                color: "#8C7DA0",
                background: "none",
                border: "none",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {t.reflectOnSomethingElse}
            </button>
            <IndiaV2ChatCta onClick={goToChat} label={shared.continueInChat} />
          </AstriaIndiaV2ResultCard>
        )}
      </AstriaIndiaV2Shell>
    );
  }

  return (
    <AstriaIndiaV2Shell
      active="aapka-note"
      lang={lang}
      onLangChange={setLang}
      version={version}
    >
      <WizardProgress
        step={step}
        total={TOTAL_STEPS}
        accent="#C9A9E8"
        stepLabel={shared.stepOf}
        percentLabel={shared.percentComplete}
      />

      {step === 1 && (
        <WizardStep
          stepKey="note"
          question={t.step1Question}
          helper={
            latestUserMessage ? t.step1HelperCaptured : t.step1HelperEmpty
          }
        >
          <WizardTextInput
            value={note}
            onChange={setNote}
            placeholder={t.notePlaceholder}
            multiline
          />
          <WizardNav
            showBack={false}
            nextDisabled={!note.trim()}
            onNext={() => setStep(2)}
            nextLabel={shared.continueLabel}
            backLabel={shared.back}
          />
        </WizardStep>
      )}

      {step === 2 && (
        <WizardStep stepKey="feeling" question={t.step2Question}>
          <WizardEmojiWheel
            value={feeling}
            onChange={setFeeling}
            labels={shared.emojiLabels}
          />
          <WizardNav
            onBack={() => setStep(1)}
            nextLabel={t.revealButton}
            nextDisabled={!feeling}
            onNext={handleSubmit}
            backLabel={shared.back}
          />
        </WizardStep>
      )}
    </AstriaIndiaV2Shell>
  );
};

export default AapkaNoteTab;
